import { Component, OnInit, OnDestroy } from '@angular/core';

// Each phrase is a list of segments so "African" can receive gradient styling
// while still being typed character-by-character like the rest of the text.
interface Segment { text: string; gradient: boolean; }

const PHRASES: { segments: Segment[]; full: string }[] = [
  {
    segments: [
      { text: 'Transforming ', gradient: false },
      { text: 'African',       gradient: true  },
      { text: ' Logistics',   gradient: false  },
    ],
    full: 'Transforming African Logistics',
  },
  {
    segments: [
      { text: 'Connecting ',  gradient: false },
      { text: 'African',      gradient: true  },
      { text: ' Commerce',   gradient: false  },
    ],
    full: 'Connecting African Commerce',
  },
];

@Component({
  selector: 'app-hero-headline-wide',
  standalone: true,
  imports: [],
  template: `
    <!--
      Full-width single-line typewriter.
      The cursor lives at the end of whatever has been typed so far —
      moving right-to-right during typing, left-to-right during erasing.
      "African" is rendered with gradient-text whenever those characters are present.
    -->
    <h1
      class="font-display font-bold leading-none mb-8 w-full reveal stagger-1"
      style="font-size: clamp(2.5rem, 6vw, 6.5rem); letter-spacing: -0.03em;"
    >
      <!-- Typed segments — rebuilt each frame -->
      @for (seg of displaySegments; track $index) {
        @if (seg.gradient) {
          <span class="gradient-text">{{ seg.text }}</span>
        } @else {
          <span style="color: var(--theme-text)">{{ seg.text }}</span>
        }
      }

      <!-- Single cursor that travels with the text -->
      <img
        src="/assets/images/icon.png"
        alt=""
        aria-hidden="true"
        [class.cursor-blink]="blinking"
        style="
          display: inline-block;
          width: clamp(18px, 2vw, 26px);
          height: clamp(18px, 2vw, 26px);
          object-fit: contain;
          vertical-align: middle;
          margin-left: 5px;
          filter: drop-shadow(0 0 8px rgba(0,140,240,0.95));
        "
      />
    </h1>
  `,
  styles: [`
    /* Block host so it doesn't affect siblings during type/erase cycles */
    :host {
      display: block;
      /* Reserve 2 lines of space so nothing below shifts during wrapping */
      min-height: calc(2.2 * clamp(2.5rem, 6vw, 6.5rem) + 2rem);
    }

    @keyframes cursor-blink {
      0%,  45% { opacity: 1; }
      55%, 100% { opacity: 0; }
    }
    .cursor-blink { animation: cursor-blink 0.9s ease-in-out infinite; }
  `],
})
export class HeroHeadlineWideComponent implements OnInit, OnDestroy {
  phraseIdx = 0;
  charCount = 0;        // how many characters of the current phrase are visible
  blinking  = false;

  private timers: ReturnType<typeof setTimeout>[] = [];

  // Rebuild visible segments from phraseIdx + charCount — no stored state
  get displaySegments(): { text: string; gradient: boolean }[] {
    const { segments } = PHRASES[this.phraseIdx];
    let remaining = this.charCount;
    const result: { text: string; gradient: boolean }[] = [];
    for (const seg of segments) {
      if (remaining <= 0) break;
      const visible = Math.min(remaining, seg.text.length);
      result.push({ text: seg.text.slice(0, visible), gradient: seg.gradient });
      remaining -= visible;
    }
    return result;
  }

  ngOnInit(): void {
    this.schedule(() => this.type(), 900);
  }

  ngOnDestroy(): void {
    this.timers.forEach(clearTimeout);
    this.timers = [];
  }

  private schedule(fn: () => void, ms: number): void {
    this.timers.push(setTimeout(fn, ms));
  }

  // ─── Type the full phrase left → right ──────────────────────────────────────

  private type(): void {
    this.blinking = false;
    const max = PHRASES[this.phraseIdx].full.length;

    const step = () => {
      if (this.charCount >= max) {
        this.blinking = true;
        this.schedule(() => this.erase(), 3600);
        return;
      }
      this.charCount++;
      this.schedule(step, 78);
    };
    step();
  }

  // ─── Erase the full phrase right → left ─────────────────────────────────────

  private erase(): void {
    this.blinking = false;

    const step = () => {
      if (this.charCount <= 0) {
        // Advance to next phrase then start typing again
        this.phraseIdx = (this.phraseIdx + 1) % PHRASES.length;
        this.schedule(() => this.type(), 380);
        return;
      }
      this.charCount--;
      this.schedule(step, 52);
    };
    step();
  }
}
